import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import PreviewNote from "./PreviewNote";
import { useNavigate } from "react-router-dom";

const COLORS = [
  "#6366F1",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#A855F7",
];

const PRIORITY_KEY = "__PRIORITY__";
const ALL_KEY = "__ALL__";
const MS_IN_DAY = 1000 * 60 * 60 * 24;

const PreviewPlan = ({ durationInYears, planData, user }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(PRIORITY_KEY);

  /* ---------------- MOCK POSTS ---------------- */

  const posts = Object.keys(planData || {})
    .filter((key) => planData[key] && planData[key].trim().length > 0)
    .map((key, index) => ({
      _id: `preview-${index}`,
      note: planData[key],
      category: key,
      isPriority: index === 0,
      status: "active",
    }));

  const priorityPosts = posts.filter((p) => p.isPriority);

  const categoryMap = {};
  posts.forEach((post) => {
    categoryMap[post.category] = (categoryMap[post.category] || 0) + 1;
  });

  const categoryChartData = Object.keys(categoryMap).map((cat) => ({
    name: cat,
    value: categoryMap[cat],
  }));

  const statusChartData = [
    { name: "Active", value: posts.length },
    { name: "Released", value: 0 },
  ];

  const filteredPosts =
    activeCategory === ALL_KEY
      ? posts
      : activeCategory === PRIORITY_KEY
      ? priorityPosts
      : posts.filter((p) => p.category === activeCategory);

  /* ---------------- TIME CORE ---------------- */

  const totalDays = durationInYears * 365;
  const startDate = new Date();
  const today = new Date();

  const daysPassed = Math.floor((today - startDate) / MS_IN_DAY);
  const daysRemaining = Math.max(totalDays - daysPassed, 0);
  const progressPercent = (daysPassed / totalDays) * 100;

  /* ---------------- RENDER ---------------- */

  return (
    <div className="five-year-dashboard dark preview-wrapper">
      {/* HEADER */}
      <h1 className="dashboard-title">
        {(user?.name?.trim()?.split(" ")[0] || "Your")}&apos;s
        <span className="plan-duration">{durationInYears} Year Vision</span>
      </h1>

      {/* CHARTS */}
      <div className="charts-wrapper">
        <div className="charts-grid">
          <div className="chart-card">
            <h3>Category Distribution</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={categoryChartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={90}
                >
                  {categoryChartData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#12151b",
                    border: "1px solid #1f2937",
                    borderRadius: "8px",
                    color: "#e5e7eb",
                    fontSize: "0.82rem",
                  }}
                />
                <Legend
                  layout="vertical"
                  align="left"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => (
                    <span style={{ color: "#9ca3af", fontSize: "0.78rem" }}>
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Status Overview</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={statusChartData}
                barSize={42}
                margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: "rgba(99, 102, 241, 0.08)" }}
                  contentStyle={{
                    backgroundColor: "#0F172A",
                    border: "none",
                    borderRadius: "8px",
                    color: "#fff",
                  }}
                />
                <Bar dataKey="value" fill="#6366F1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TIME CORE */}
        <div className="time-core">
          <svg className="progress-ring" width="140" height="140">
            <circle className="progress-ring-bg" cx="70" cy="70" r="60" />
            <circle
              className="progress-ring-progress"
              cx="70"
              cy="70"
              r="60"
              style={{
                strokeDasharray: 377,
                strokeDashoffset: 377 - (377 * progressPercent) / 100,
              }}
            />
          </svg>
          <div className="time-core-center">
            <div className="time-core-value">{daysRemaining}</div>
            <div className="time-core-label">days left</div>
          </div>
        </div>
      </div>

      <button
        className="space-view-btn"
        onClick={() => navigate(`/dashboard/plan-cosmos/695a9b406980276cc3e2ce62`)}
      >
        <div className="space-view-stars" />
        <div className="space-view-stars space-view-stars-2" />
        <div className="space-view-stars space-view-stars-3" />
        <span className="space-view-label">Dummy Space View</span>
      </button>
      {/* CATEGORY SELECTOR */}
      <div className="category-selector">
        <h3>Explore Records by Category</h3>

        <div className="category-tags">
          {/* ALL */}
          <button
            className={`category-btn ${activeCategory === ALL_KEY ? "active" : ""}`}
            onClick={() => setActiveCategory(ALL_KEY)}
          >
            All ({posts.length})
          </button>

          {/* PRIORITY */}
          <button
            className={`category-btn priority-btn ${
              activeCategory === PRIORITY_KEY ? "active" : ""
            }`}
            onClick={() => setActiveCategory(PRIORITY_KEY)}
          >
            Priority ({priorityPosts.length})
          </button>

          {/* CATEGORIES */}
          {Object.keys(categoryMap).map((cat) => (
            <button
              key={cat}
              className={`category-btn ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat} ({categoryMap[cat]})
            </button>
          ))}
        </div>
      </div>

      {/* ADD NODE CTA (Disabled) */}
      <div className="add-node-cta">
        <p className="add-node-text">Want to add more nodes to this plan?</p>

        <div className="add-node-actions">
          <button className="add-node-btn primary" disabled>
            Add to Category
          </button>

          <button className="add-node-btn secondary" disabled>
            Create New Record
          </button>
        </div>
      </div>

      {/* POSTS */}
      <div className="dashboard-section">
        <h3 className="section-title">
          {activeCategory === ALL_KEY
            ? "All Records"
            : activeCategory === PRIORITY_KEY
            ? "Priority Records"
            : activeCategory}
        </h3>

        <div className="preview-notes-grid">
          {filteredPosts.length === 0 ? (
            <p className="empty-state">No nodes in this category.</p>
          ) : (
            filteredPosts.map((post) => (
              <PreviewNote key={post._id} post={post} dark={true} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewPlan;
